"use client";

import { useEffect, useRef, type PropsWithChildren } from "react";
import { useParams } from "next/navigation";

import { useSession } from "@/hooks/useSession";
import {
  createAssistantSession,
  createAssistantSessionNew,
  openAssistantStream,
  sendAssistantMessage,
} from "@/lib/assistant-api";
import { persistDraft, readDraft } from "@/features/instant-assistant/storage";
import { useNamespacesQuery } from "@/hooks/useNamespaces";
import { useNamespaceStore } from "@/state/namespace-store";

import { AssistantOverlay } from "./AssistantOverlay";
import { fromServerMessage, useAssistantOverlay } from "./useAssistantOverlay";


const DRAFT_PERSIST_DEBOUNCE_MS = 250;

/**
 * Mounts the floating assistant overlay (v0 sketch) globally and wires the
 * keyboard-only wake-up paths:
 *
 *   1. Cmd/Ctrl + K           — announced shortcut, works from any focus
 *                                context including text inputs / editors;
 *                                browser default (URL bar) is suppressed.
 *   2. Double-tap Shift       — discovered gesture, 300ms window between
 *                                first Shift release and second Shift
 *                                press, invalidated by any non-Shift key
 *                                in between.
 *
 * The former top-bar launcher button was removed on purpose — the assistant
 * is reachable through the keyboard only. Both paths are toggles. Esc also
 * closes from any focus context.
 *
 * Focus management: when the overlay opens we capture whatever element was
 * focused, and when it closes we restore focus to that element. Cursor
 * position in text inputs is preserved by re-focusing the same element.
 */

const SHIFT_DOUBLE_TAP_WINDOW_MS = 300;

type ShiftStage = "idle" | "shift1-down" | "shift1-up";

export function AssistantOverlayProvider({ children }: PropsWithChildren) {
  const toggle = useAssistantOverlay((s) => s.toggle_);
  const close_ = useAssistantOverlay((s) => s.close_);
  const open = useAssistantOverlay((s) => s.open);
  const sessionId = useAssistantOverlay((s) => s.sessionId);
  const setSessionId = useAssistantOverlay((s) => s.setSessionId);
  const replaceMessages = useAssistantOverlay((s) => s.replaceMessages);
  const appendServerMessage = useAssistantOverlay((s) => s.appendServerMessage);
  const setSubmitHandler = useAssistantOverlay((s) => s.setSubmitHandler);
  const clearLiveSession = useAssistantOverlay((s) => s.clearLiveSession);
  const setStatus = useAssistantOverlay((s) => s.setStatus);
  const draft = useAssistantOverlay((s) => s.draft);
  const setDraft = useAssistantOverlay((s) => s.setDraft);
  const sessionResetTick = useAssistantOverlay((s) => s.sessionResetTick);

  // Auth + namespace gate live mode. When either is missing, the overlay
  // stays in demo mode (seed thread + local placeholder echo) so the
  // /preview/* pages keep working without auth.
  const { session } = useSession();
  const profile = session.status === "authenticated" ? session.profile : null;
  const userId = profile?.subject ?? null;
  const operatorName = profile?.name?.trim()?.length
    ? profile.name!.trim()
    : profile?.email?.trim()?.length
      ? profile.email!.trim()
      : "Operator";
  const operatorAvatarUrl = profile?.pictureUrl ?? undefined;
  // Resolve the active namespace from the route's [slug] when present. On
  // direct navigation to /{slug}/... the store can be null or stale, so the
  // path is the canonical source on those routes; everywhere else we fall
  // back to the store. Slug → namespaceId resolution lives in the workspace
  // hydrator, but the assistant overlay also runs on routes that don't
  // mount that hydrator yet, so we resolve via the namespaces list here.
  const params = useParams();
  const rawSlug = params?.slug;
  const pathSlug =
    typeof rawSlug === "string"
      ? rawSlug
      : Array.isArray(rawSlug) && rawSlug.length > 0
        ? rawSlug[0]
        : null;
  const namespacesQuery = useNamespacesQuery();
  const namespaceFromPath = pathSlug
    ? namespacesQuery.data?.find((n) => (n.slug ?? "").toLowerCase() === pathSlug.toLowerCase())
    : undefined;
  const storeNamespaceId = useNamespaceStore((state) => state.selectedNamespaceId);
  const namespaceId = namespaceFromPath?.namespaceId ?? storeNamespaceId;
  const isAuthenticated = userId !== null;

  // Track where focus was when we opened so we can restore it on close.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Track which scope we last bootstrapped so namespace/user changes
  // tear down the previous session cleanly. resetTick mirrors the store's
  // `sessionResetTick` — the user-triggered "new session" bumps it, and
  // the bootstrap effect re-fires when the tick diverges from the scope
  // we last started a session for.
  const liveScopeRef = useRef<{
    namespaceId: string | null;
    userId: string | null;
    resetTick: number;
  }>({
    namespaceId: null,
    userId: null,
    resetTick: 0,
  });

  // Track which scope we last hydrated the draft for so we don't re-read
  // (and potentially clobber typing) on every render.
  const hydratedDraftScopeRef = useRef<{ namespaceId: string | null; userId: string | null }>({
    namespaceId: null,
    userId: null,
  });

  useEffect(() => {
    if (open) {
      const active = document.activeElement;
      if (
        active &&
        active !== document.body &&
        active instanceof HTMLElement &&
        // Ignore focus already inside the overlay (e.g. a re-open while
        // the overlay's own composer was somehow focused).
        !active.closest('[data-overlay="assistant"]')
      ) {
        previousFocusRef.current = active;
      }
    } else if (previousFocusRef.current) {
      const target = previousFocusRef.current;
      previousFocusRef.current = null;
      // Defer one tick so React has finished unmounting the overlay's
      // focus traps before we move focus back.
      window.setTimeout(() => {
        try {
          target.focus({ preventScroll: true });
        } catch {
          // The element may have unmounted while the overlay was open.
        }
      }, 0);
    }
  }, [open]);

  // Wake-up keyboard handlers.
  useEffect(() => {
    let shiftStage: ShiftStage = "idle";
    let shiftTimer: number | null = null;

    const resetShift = () => {
      shiftStage = "idle";
      if (shiftTimer != null) {
        window.clearTimeout(shiftTimer);
        shiftTimer = null;
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const isMod = event.metaKey || event.ctrlKey;

      // Cmd/Ctrl + K — works from any focus context. preventDefault so the
      // browser doesn't steal focus to the URL bar's search.
      if (isMod && (event.key === "k" || event.key === "K")) {
        event.preventDefault();
        resetShift();
        toggle();
        return;
      }

      // Esc closes from any focus context.
      if (event.key === "Escape" && open) {
        event.preventDefault();
        close_();
        resetShift();
        return;
      }

      // Shift double-tap state machine.
      if (event.key === "Shift") {
        // Held-shift autorepeat — ignore.
        if (event.repeat) return;
        if (shiftStage === "shift1-up") {
          // Second Shift down inside the window — wake up.
          resetShift();
          toggle();
        } else {
          // Either fresh start or Shift pressed again before keyup of the
          // first one (modifier chord); arm for first-shift-down.
          shiftStage = "shift1-down";
        }
        return;
      }

      // Any other key invalidates an in-flight Shift gesture.
      if (shiftStage !== "idle") {
        resetShift();
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key !== "Shift") return;
      if (shiftStage === "shift1-down") {
        shiftStage = "shift1-up";
        if (shiftTimer != null) window.clearTimeout(shiftTimer);
        shiftTimer = window.setTimeout(() => {
          shiftStage = "idle";
          shiftTimer = null;
        }, SHIFT_DOUBLE_TAP_WINDOW_MS);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      if (shiftTimer != null) window.clearTimeout(shiftTimer);
    };
  }, [toggle, close_, open]);

  // Bootstrap a backend session when the overlay opens with auth + namespace.
  // Resets to demo when either condition breaks (logout, namespace switch).
  useEffect(() => {
    if (!isAuthenticated || !namespaceId) {
      // Tear down any prior live session before going back to demo.
      if (liveScopeRef.current.namespaceId !== null || liveScopeRef.current.userId !== null) {
        liveScopeRef.current = { namespaceId: null, userId: null, resetTick: sessionResetTick };
        clearLiveSession();
      }
      return;
    }
    if (!open) return;
    if (sessionId) return;
    // If scope already matches and we're just re-opening, nothing to do.
    // A user-triggered "new session" bumps sessionResetTick so this skip
    // doesn't apply and we re-bootstrap into a fresh backend session.
    if (
      liveScopeRef.current.namespaceId === namespaceId &&
      liveScopeRef.current.userId === userId &&
      liveScopeRef.current.resetTick === sessionResetTick
    ) {
      return;
    }

    // A user-initiated reset shows up as: same (namespace, user) we
    // already bootstrapped for, but a newer resetTick. In that case we
    // must call /sessions/new — the default /sessions endpoint
    // resume-or-creates and would just hand back the same session id +
    // transcript we were trying to leave behind, so the new-session
    // button would silently no-op. Initial bootstrap and namespace
    // switches still go through the resume-aware path.
    const isUserInitiatedReset =
      liveScopeRef.current.namespaceId === namespaceId &&
      liveScopeRef.current.userId === userId &&
      liveScopeRef.current.resetTick !== sessionResetTick;

    const controller = new AbortController();
    let cancelled = false;
    liveScopeRef.current = { namespaceId, userId, resetTick: sessionResetTick };

    void (async () => {
      try {
        const create = isUserInitiatedReset
          ? createAssistantSessionNew
          : createAssistantSession;
        const response = await create(
          {
            namespaceId,
            operator: {
              id: userId,
              displayName: operatorName,
              avatarUrl: operatorAvatarUrl,
            },
          },
          controller.signal,
        );
        if (cancelled) return;
        // Hydrate the overlay with the server transcript (existing turns
        // for this operator + namespace) and switch to live mode.
        const overlayMessages = response.transcript
          .map(fromServerMessage)
          .filter((m): m is NonNullable<ReturnType<typeof fromServerMessage>> => m !== null);
        replaceMessages(overlayMessages);
        setSessionId(response.sessionId);
      } catch (error) {
        if (!cancelled) {
          console.warn("Assistant overlay: session bootstrap failed", error);
          // Stay in demo mode rather than show a hard error for v0.
          liveScopeRef.current = { namespaceId: null, userId: null, resetTick: sessionResetTick };
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    isAuthenticated,
    namespaceId,
    userId,
    operatorName,
    operatorAvatarUrl,
    open,
    sessionId,
    sessionResetTick,
    clearLiveSession,
    replaceMessages,
    setSessionId,
  ]);

  // Open the SSE stream once we have a live session. New messages, queue
  // updates, and status events flow into the store. Closes on session
  // teardown or unmount.
  useEffect(() => {
    if (!sessionId) return;
    // No `since` option for v0 — initial transcript came from the
    // create-session response. Catch-up on reconnect is a follow-up.
    const stop = openAssistantStream(sessionId, {
      onMessage: (message) => {
        appendServerMessage(message);
        // Once an assistant message lands, the turn is done; flip the
        // indicator off in case the backend hasn't yet sent an idle
        // status event.
        if (message.role === "assistant") {
          setStatus({ kind: "idle", detail: null });
        }
      },
      onStatus: (status) => {
        // Map the backend's InstantAssistantStatusEvent shape onto the
        // overlay's narrower OverlayStatus.
        setStatus({
          kind: status.kind,
          detail: status.detail ?? null,
        });
      },
      onError: (error) => {
        console.warn("Assistant overlay: SSE error", error);
        setStatus({ kind: "error", detail: null });
      },
    });
    return () => {
      stop();
    };
  }, [sessionId, appendServerMessage, setStatus]);

  // Register the live-mode submit handler. send() in the store delegates to
  // it when a session exists; demo mode keeps the local placeholder echo.
  useEffect(() => {
    if (!sessionId || !namespaceId) {
      setSubmitHandler(null);
      return;
    }
    setSubmitHandler((content) => {
      void sendAssistantMessage(sessionId, {
        content,
        namespaceId,
        // The new overlay has no context-attachment UI yet, so every
        // turn opts out of the workspace-context requirement. The
        // backend's validation otherwise rejects with "Attach workspace
        // context or opt out before sending." and the SSE delivers the
        // error event with no message reply, leaving the user staring
        // at a hung thinking indicator.
        contextOptOut: true,
      }).catch((error) => {
        console.warn("Assistant overlay: send failed", error);
      });
    });
    return () => {
      setSubmitHandler(null);
    };
  }, [sessionId, namespaceId, setSubmitHandler]);

  // Hydrate the composer draft from IndexedDB whenever the (namespace,
  // user) scope changes. Reads the same store the legacy panel writes
  // to so drafts survive page reloads and follow the operator across
  // surfaces.
  useEffect(() => {
    if (!isAuthenticated || !namespaceId) return;
    const scope = hydratedDraftScopeRef.current;
    if (scope.namespaceId === namespaceId && scope.userId === userId) return;
    hydratedDraftScopeRef.current = { namespaceId, userId };

    let cancelled = false;
    void readDraft(namespaceId, userId).then((stored) => {
      if (cancelled) return;
      // Only restore if the store is still empty — don't wipe text the
      // user started typing during the IndexedDB read race.
      const currentDraft = useAssistantOverlay.getState().draft;
      if (currentDraft === "" && stored && stored.text) {
        setDraft(stored.text);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, namespaceId, userId, setDraft]);

  // Persist the composer draft on change (debounced). When scope changes
  // the cleanup cancels the in-flight write so a stale draft never lands
  // under the new scope's key.
  useEffect(() => {
    if (!isAuthenticated || !namespaceId) return;
    const handle = window.setTimeout(() => {
      void persistDraft(namespaceId, { text: draft, context: [] }, userId).catch(
        (error) => {
          console.warn("Assistant overlay: draft persist failed", error);
        },
      );
    }, DRAFT_PERSIST_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [draft, isAuthenticated, namespaceId, userId]);

  return (
    <>
      {children}
      <AssistantOverlay />
    </>
  );
}
